import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './login.css';
import { useSubscription } from '../subscription/subscriptioncontext';

function Login({ onLoginSuccess }) {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const navigate = useNavigate();
  const { refreshSubscription } = useSubscription();
  
  // Get API base URL from environment variable
  const apiBaseUrl = process.env.REACT_APP_RAILWAY_URL || 'http://localhost:8000';
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
    setApiError('');
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    return newErrors;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    setErrors(validationErrors);
    
    if (Object.keys(validationErrors).length === 0) {
      setIsSubmitting(true);
      setApiError('');
      
      try {
        const response = await fetch(`${apiBaseUrl}/api/login/`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: formData.email,
            password: formData.password
          }),
        }).catch(() => {
          // Catch network errors to prevent console logging
          throw new Error('Network error. Please check your connection.');
        });

        const data = await response.json();

        if (!response.ok) {
          // Handle error responses without throwing to prevent console errors
          setApiError(data.error || 'Login failed');
          return;
        }

        // Debug logging
        console.log('🔵 LOGIN - Data received from API:', data);
        console.log('🔵 LOGIN - User data being stored:', data.user);
        
        // Store user data and login state
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isLoggedIn', 'true');
        
        // Trigger subscription refresh for the new user
        refreshSubscription();
        setLoginSuccess(true);
        
        // Redirect to dashboard after successful login
        setTimeout(() => {
          if (onLoginSuccess) {
            onLoginSuccess();
          }
          navigate('/dashboard');
        }, 1500);
      } catch (error) {
        setApiError(error.message);
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/images/Pennylogga.png" alt="Penny Logo" className="login-logo" />
          <h2>Welcome Back</h2>
          <p>Sign in to your Penny account</p>
        </div>
        
        {loginSuccess ? (
          <div className="login-success">
            <i className="fas fa-check-circle"></i>
            <p>Login successful! Redirecting...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login-form">
            {apiError && <div className="api-error">{apiError}</div>}
            
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={errors.email ? 'input-error' : ''}
                placeholder="Enter your email"
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className={errors.password ? 'input-error' : ''}
                placeholder="Enter your password"
              />
              {errors.password && <span className="error-message">{errors.password}</span>}
            </div>
            
            <div className="form-options">
              <Link to="/forgot-password" className="forgot-password">
                Forgot password?
              </Link>
            </div>
            
            <button 
              type="submit" 
              className="login-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Signing in...' : 'Sign In'}
            </button>
            
            {/* TODO: Implement Google Sign-In
            <div className="login-divider">
              <span>OR</span>
            </div>
            
            <button type="button" className="google-login-button">
              <i className="fab fa-google"></i>
              Sign in with Google
            </button>
            */}
            
            <div className="signup-link">
              <p>
                Don't have an account? <Link to="/signup">Sign up</Link>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login; 